import React from 'react';
import { Link } from 'react-router-dom';

function About() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-violet-50 via-purple-50 to-pink-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4 bg-gradient-to-r from-purple-600 via-pink-600 to-violet-600 bg-clip-text text-transparent">
            About CryptoVista
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Your comprehensive cryptocurrency market tracking platform powered by real-time data from Binance
          </p>
        </div>

        {/* Hero Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
          <div className="bg-white rounded-xl p-6 text-center shadow-lg border border-gray-100">
            <div className="text-3xl font-bold text-purple-600 mb-2">100+</div>
            <div className="text-gray-600 text-sm">Cryptocurrencies</div>
          </div>
          <div className="bg-white rounded-xl p-6 text-center shadow-lg border border-gray-100">
            <div className="text-3xl font-bold text-green-600 mb-2">24/7</div>
            <div className="text-gray-600 text-sm">Live Updates</div>
          </div>
          <div className="bg-white rounded-xl p-6 text-center shadow-lg border border-gray-100">
            <div className="text-3xl font-bold text-purple-600 mb-2">100%</div>
            <div className="text-gray-600 text-sm">Free Access</div>
          </div>
          <div className="bg-white rounded-xl p-6 text-center shadow-lg border border-gray-100">
            <div className="text-3xl font-bold text-orange-600 mb-2">0</div>
            <div className="text-gray-600 text-sm">Registration</div>
          </div>
        </div>

        {/* Main Content */}
        <div className="bg-white rounded-2xl shadow-xl p-8 sm:p-12 mb-8 border border-gray-100">
          <section className="mb-10">
            <h2 className="text-3xl font-bold text-gray-900 mb-4 flex items-center">
              <span className="mr-3 text-4xl">📊</span>
              What We Do
            </h2>
            <p className="text-gray-700 leading-relaxed text-lg mb-4">
              CryptoVista is a powerful, real-time cryptocurrency market tracking application that provides 
              users with comprehensive data and insights about digital assets traded on the Binance exchange. 
              Our platform offers an intuitive interface to monitor market trends, track price movements, and 
              analyze trading volumes across hundreds of cryptocurrencies.
            </p>
            <p className="text-gray-700 leading-relaxed text-lg">
              Built with modern web technologies, we deliver accurate, up-to-the-minute market data directly 
              from Binance's public API, ensuring you always have access to the latest information when making 
              investment decisions.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-3xl font-bold text-gray-900 mb-4 flex items-center">
              <span className="mr-3 text-4xl">🎯</span>
              Our Mission
            </h2>
            <p className="text-gray-700 leading-relaxed text-lg mb-4">
              Our mission is to democratize access to cryptocurrency market data by providing a free, 
              user-friendly platform that makes complex market information accessible to everyone, from 
              seasoned traders to crypto enthusiasts just starting their journey.
            </p>
            <p className="text-gray-700 leading-relaxed text-lg">
              We believe that informed decision-making is the key to successful investing, and we're 
              committed to providing the tools and data you need to navigate the dynamic world of 
              cryptocurrency markets with confidence.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-3xl font-bold text-gray-900 mb-4 flex items-center">
              <span className="mr-3 text-4xl">✨</span>
              Key Features
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-6 border border-purple-100 hover:shadow-lg transition-shadow">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Real-Time Data</h3>
                <p className="text-gray-700">
                  Get live updates on cryptocurrency prices, volumes, and market movements updated every 30 seconds.
                </p>
              </div>
              <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-6 border border-green-100 hover:shadow-lg transition-shadow">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Comprehensive Search</h3>
                <p className="text-gray-700">
                  Quickly find any cryptocurrency by searching for its symbol or name with our intuitive search functionality.
                </p>
              </div>
              <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-6 border border-purple-100 hover:shadow-lg transition-shadow">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Detailed Analytics</h3>
                <p className="text-gray-700">
                  Access in-depth coin details including 24-hour price history, trading volumes, and market statistics.
                </p>
              </div>
              <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-xl p-6 border border-orange-100 hover:shadow-lg transition-shadow">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">User-Friendly Interface</h3>
                <p className="text-gray-700">
                  Enjoy a beautiful, responsive design that works seamlessly on desktop, tablet, and mobile devices.
                </p>
              </div>
            </div>
          </section>

          <section className="mb-10">
            <h2 className="text-3xl font-bold text-gray-900 mb-4 flex items-center">
              <span className="mr-3 text-4xl">🔒</span>
              Data Security & Privacy
            </h2>
            <p className="text-gray-700 leading-relaxed text-lg mb-4">
              We take your privacy seriously. CryptoVista is a read-only application that connects directly 
              to Binance's public API. We do not store any personal information, trading data, or require user 
              accounts. All data is fetched in real-time and displayed securely in your browser.
            </p>
            <p className="text-gray-700 leading-relaxed text-lg">
              Since we don't handle any sensitive information or require authentication, you can use our platform 
              with complete peace of mind, knowing that your privacy is protected.
            </p>
          </section>

          {/* <section>
            <h2 className="text-3xl font-bold text-gray-900 mb-4 flex items-center">
              <span className="mr-3 text-4xl">🚀</span>
              Technology Stack
            </h2>
            <p className="text-gray-700 leading-relaxed text-lg mb-6">
              CryptoVista is built using cutting-edge web technologies to ensure fast performance, 
              reliability, and an excellent user experience:
            </p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="flex items-center space-x-3 bg-gray-50 rounded-lg p-4 hover:bg-gray-100 transition-colors">
                <span className="text-3xl">⚛️</span>
                <div>
                  <p className="font-semibold text-gray-900">React</p>
                  <p className="text-sm text-gray-600">Modern UI framework</p>
                </div>
              </div>
              <div className="flex items-center space-x-3 bg-gray-50 rounded-lg p-4 hover:bg-gray-100 transition-colors">
                <span className="text-3xl">🎨</span>
                <div>
                  <p className="font-semibold text-gray-900">Tailwind CSS</p>
                  <p className="text-sm text-gray-600">Utility-first styling</p>
                </div>
              </div>
              <div className="flex items-center space-x-3 bg-gray-50 rounded-lg p-4 hover:bg-gray-100 transition-colors">
                <span className="text-3xl">🔄</span>
                <div>
                  <p className="font-semibold text-gray-900">React Router</p>
                  <p className="text-sm text-gray-600">Navigation & routing</p>
                </div>
              </div>
              <div className="flex items-center space-x-3 bg-gray-50 rounded-lg p-4 hover:bg-gray-100 transition-colors">
                <span className="text-3xl">📡</span>
                <div>
                  <p className="font-semibold text-gray-900">Binance API</p>
                  <p className="text-sm text-gray-600">Real-time market data</p>
                </div>
              </div>
            </div>
          </section> */}
        </div>

        {/* Benefits Section */}
          <div className="bg-gradient-to-r from-purple-600 via-pink-600 to-violet-600 rounded-2xl shadow-xl p-8 sm:p-12 mb-8 text-white">
          <h2 className="text-3xl sm:text-4xl font-bold mb-8 text-center">Why Users Love Binance Tracker</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-white bg-opacity-20 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold mb-2">Instant Access</h3>
              <p className="text-purple-100">No sign-up required. Start tracking markets immediately.</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-white bg-opacity-20 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold mb-2">Accurate Data</h3>
              <p className="text-purple-100">Direct connection to Binance ensures data accuracy.</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-white bg-opacity-20 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold mb-2">Mobile Ready</h3>
              <p className="text-purple-100">Fully responsive design works on all devices.</p>
            </div>
          </div>
        </div>

        {/* Call to Action */}
        <div className="bg-white rounded-2xl shadow-xl p-8 sm:p-12 text-center border border-gray-100">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Ready to Explore the Markets?</h2>
          <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
            Start tracking your favorite cryptocurrencies today and make informed investment decisions!
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/"
              className="px-8 py-3 bg-gradient-to-r from-purple-600 via-pink-600 to-violet-600 text-white rounded-lg font-semibold hover:from-purple-700 hover:via-pink-700 hover:to-violet-700 transition-all duration-200 shadow-lg hover:shadow-xl"
            >
              View Market Data
            </Link>
            <Link
              to="/faq"
              className="px-8 py-3 bg-gray-100 text-gray-700 rounded-lg font-semibold hover:bg-gray-200 transition-colors"
            >
              Read FAQ
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

export default About;
